import React, { useCallback, useEffect, useState } from 'react';
import {
  selectHMSMessages,
  selectLocalPeerID,
  selectPeers,
  selectTracksMap,
  useHMSActions,
  useHMSVanillaStore,
} from '@100mslive/react-sdk';
import { PipIcon } from '@100mslive/react-icons';
import { Flex, Tooltip } from '../../..';
import IconButton from '../../IconButton';
// @ts-ignore: No implicit Any
import { ToastManager } from '../Toast/ToastManager';
import { PictureInPicture } from './PIPManager';
import { pickIncomingMessage } from './pipMessageUtils';
// @ts-ignore: No implicit Any
import { MediaSession } from './SetupMediaSession';
// @ts-ignore: No implicit Any
import { usePinnedTrack } from '../AppData/useUISettings';

/**
 * shows a button which when clicked shows some videos in PIP, clicking
 * again turns it off.
 */
const PIPComponent = ({ content = null }) => {
  const [isPipOn, setIsPipOn] = useState(PictureInPicture.isOn());
  const hmsActions = useHMSActions();
  const store = useHMSVanillaStore();

  const onPipToggle = useCallback(() => {
    if (!isPipOn) {
      PictureInPicture.start(hmsActions, setIsPipOn).catch(err => {
        console.error('error in starting pip', err);
        ToastManager.addToast({ title: 'Could not start Picture-in-Picture', variant: 'error' });
      });
      MediaSession.setup(hmsActions, store);
    } else {
      PictureInPicture.stop().catch(err => console.error('error in stopping pip', err));
    }
  }, [hmsActions, isPipOn, store]);

  useEffect(() => {
    if (!isPipOn) {
      // pre-warm the pip media elements so the request on click stays within
      // Safari's user-activation window (see PipManager.warmup)
      PictureInPicture.warmup().catch(err => console.error('error in pip warmup', err));
    }
  }, [isPipOn]);

  if (!PictureInPicture.isSupported()) {
    return null;
  }
  return (
    <>
      {content ? (
        <Flex css={{ w: '100%' }} onClick={() => onPipToggle()} data-testid="pip_btn">
          {content}
        </Flex>
      ) : (
        <Tooltip title={`${isPipOn ? 'Deactivate' : 'Activate'} picture in picture view`}>
          <IconButton active={!isPipOn} key="pip" onClick={() => onPipToggle()} data-testid="pip_btn">
            <PipIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

/**
 * this is a separate component so it can be conditionally rendered and
 * the subscriptions to store are done only if required.
 */
export const ActivatedPIP = () => {
  const store = useHMSVanillaStore();
  const pinnedTrack = usePinnedTrack();

  useEffect(() => {
    function subscribeToStore() {
      const unsubscribeTracks = store.subscribe(tracksMap => {
        let pipPeers = store.getState(selectPeers);
        if (pinnedTrack) {
          pipPeers = pipPeers.filter(peer => pinnedTrack.peerId === peer.id);
        }
        PictureInPicture.updatePeersAndTracks(pipPeers, tracksMap).catch(err => {
          console.error('error in updating pip', err);
        });
      }, selectTracksMap);

      // Show incoming chat messages (from other peers) as a transient bubble on
      // the PIP canvas. Seed with the latest existing message id so only messages
      // that arrive after PIP starts trigger a bubble.
      let lastShownMessageId: string | undefined = store.getState(selectHMSMessages)?.slice(-1)[0]?.id;
      const unsubscribeMessages = store.subscribe(messages => {
        try {
          const result = pickIncomingMessage(messages, lastShownMessageId, store.getState(selectLocalPeerID));
          lastShownMessageId = result.lastShownMessageId;
          if (result.message) {
            PictureInPicture.setLatestMessage(result.message);
          }
        } catch (err) {
          console.error('error surfacing pip chat message', err);
        }
      }, selectHMSMessages);

      return () => {
        unsubscribeTracks();
        unsubscribeMessages();
      };
    }
    let unsubscribe: (() => void) | undefined = PictureInPicture.isOn() ? subscribeToStore() : undefined;
    PictureInPicture.listenToStateChange(isOn => {
      if (isOn) {
        if (!unsubscribe) {
          unsubscribe = subscribeToStore();
        }
      } else {
        unsubscribe?.();
        unsubscribe = undefined;
      }
    });
    return () => {
      unsubscribe?.();
      unsubscribe = undefined;
    };
  }, [pinnedTrack, store]);

  return <></>;
};

export default PIPComponent;
